import { Box, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { ButtonWrap, Icon, IconName, useDeviceTypes } from "halifax";
import { RecentExperienceSearch } from "redmond";
import { getExperiencesAvailabilityQuery } from "@capone/common";

import "./styles.scss";
import { EXPERIENCES_ENTRY } from "redmond/build/apis";
import { ClientContext } from "../../App";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  PORTAL_TITLE,
  SEARCH_EXPERIENCES_TITLE,
} from "../../lang/textConstants";
import { ExperiencesSearchConnectorProps } from "./container";
import { ExperiencesSearchControl } from "./components/ExperiencesSearchControl";
import { MobileExperiencesSearchControl } from "./components/MobileExperiencesSearchControl";
import { ExperiencesInfoModal } from "./components/ExperiencesInfoModal";
import {
  INFO_MODAL_CTA_TEXT,
  SUBTITLE,
  getEarnMessageToDisplay,
  INFO_MODAL_STORAGE_KEY,
} from "./textConstants";
import { getExperiencesEntryProperties } from "./reducer";
import { PATH_AVAILABILITY } from "../../utils/paths";

export interface IExperiencesSearchProps
  extends ExperiencesSearchConnectorProps,
    RouteComponentProps {}

export const ExperiencesSearch = ({
  fetchRewardsAccounts,
  largestValueAccount,
  fetchRewardsAccountsCallState,
  setLocation,
}: IExperiencesSearchProps) => {
  const { sessionInfo } = useContext(ClientContext);
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const history = useHistory();

  const [experiencesInfoModalOpen, setExperiencesInfoModalOpen] =
    useState(false);
  const [recentSearches, setRecentSearches] = useState<
    RecentExperienceSearch[]
  >([]);

  const modalSeen = localStorage.getItem(INFO_MODAL_STORAGE_KEY) != null;

  const properties = useSelector(getExperiencesEntryProperties);

  useEffect(() => {
    document.title = SEARCH_EXPERIENCES_TITLE;
    fetchRewardsAccounts(false, sessionInfo);
    setTimeout(() => window.scrollTo(0, 0), 0);

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [fetchRewardsAccounts, sessionInfo]);

  // Separate useEffect to track event after rewards accounts are loaded
  useEffect(() => {
    if (properties.properties.rewards_accounts) {
      trackEvent({
        eventName: EXPERIENCES_ENTRY,
        properties: {
          ...properties.properties,
        },
        encryptedProperties: properties.encryptedProperties,
      });
    }
  }, [properties]);

  useEffect(() => {
    const getRecentSearches = () => {
      try {
        const localStorageItem = localStorage.getItem(
          "recently_searched_experiences"
        );

        if (localStorageItem) {
          const parsedSearches: RecentExperienceSearch[] =
            JSON.parse(localStorageItem);
          const sortedSearches = parsedSearches
            .filter((search) => !!search.location)
            .sort(
              (a, b) =>
                new Date(b.searchDate).getTime() -
                new Date(a.searchDate).getTime()
            );
          setRecentSearches(sortedSearches);
        }
      } catch (e) {
        console.error(`Failed to get recent searches from local storage: ${e}`);
      }
    };

    getRecentSearches();

    window.addEventListener(
      "update_recently_searched_experiences",
      getRecentSearches
    );

    return () =>
      window.removeEventListener(
        "update_recently_searched_experiences",
        getRecentSearches
      );
  }, []);

  const onRecentSearchClick = useCallback(
    (search: RecentExperienceSearch) => {
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          search.locationLabel,
          search.fromDate,
          search.untilDate || search.fromDate
        )}`
      );
    },

    [history]
  );

  return (
    <>
      <Box
        className={clsx("experiences-search-root", {
          desktop: matchesDesktop,
          mobile: matchesMobile,
          "mobile-experiences-search-root": matchesMobile,
        })}
      >
        {matchesMobile ? (
          <Box>
            <MobileExperiencesSearchControl
              onSelectLocation={setLocation}
              recentSearches={recentSearches}
              onRecentSearchClick={onRecentSearchClick}
            />
            <ButtonWrap
              className="experiences-info-cta mobile"
              onClick={() => setExperiencesInfoModalOpen(true)}
            >
              <Box className="new-tag">New</Box>
              <Typography className="experiences-info-cta-text mobile">
                {INFO_MODAL_CTA_TEXT}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </ButtonWrap>
          </Box>
        ) : (
          <Box className="experiences-search-container">
            <Box className="experiences-search-titles-and-fields-wrapper">
              <Typography variant="h1" className="search-title">
                {getEarnMessageToDisplay(
                  fetchRewardsAccountsCallState,
                  largestValueAccount,
                  true
                )}
              </Typography>
              <Typography variant="h2" className="search-subtitle">
                {SUBTITLE}
              </Typography>
              <ExperiencesSearchControl
                recentSearches={recentSearches}
                onRecentSearchClick={onRecentSearchClick}
              />
              <ButtonWrap
                className="experiences-info-cta"
                onClick={() => setExperiencesInfoModalOpen(true)}
              >
                <Box className="new-tag">New</Box>
                <Typography className="experiences-info-cta-text">
                  {INFO_MODAL_CTA_TEXT}
                </Typography>
                <Icon name={IconName.InfoCircle} />
              </ButtonWrap>
            </Box>
          </Box>
        )}
      </Box>
      <ExperiencesInfoModal
        isOpen={experiencesInfoModalOpen}
        setOpen={setExperiencesInfoModalOpen}
        modalSeen={modalSeen}
      />
    </>
  );
};
